import axios from "axios";
import { createWebHistory, createRouter } from "vue-router";
import routes from "./routes";
import appConfig from "../../app.config";
import prService from "@/services/prService";
import Swal from "sweetalert2";
import { showAccessDeniedAlert } from "@/utils/accessAlert";

// Global axios config
axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["Accept"] = "application/json";

// Import auth store for interceptors
import { useAuthStore } from "@/stores/auth";

// Axios request interceptor - attach token to all requests
axios.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Axios response interceptor - handle 401 globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip auto-logout on the /auth endpoint — the router's beforeEach
    // handles the redirect with the actual error message from the response.
    const isAuthEndpoint = error.config?.url?.includes("/auth");
    if (error.response?.status === 401 && !isAuthEndpoint) {
      const authStore = useAuthStore();
      authStore.logout();
    }
    return Promise.reject(error);
  },
);

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 };
  },
});

// Helper function to extract user's dashboard permissions
function getUserDashboardAccess(user) {
  if (!user) {
    return { mainAccess: false, procurementAccess: false, hasPrUserRecord: false };
  }

  const prUser = user.pr_user || (user.permissions ? user : null);
  const permissions = prUser?.permissions || user.permissions;

  if (permissions) {
    const mainAccess = permissions.main_administrator_dashboard?.access === true;
    const procurementAccess = permissions.procurement_dashboard?.access === true;
    return { mainAccess, procurementAccess, hasPrUserRecord: true };
  }

  return { mainAccess: false, procurementAccess: false, hasPrUserRecord: false };
}

router.beforeEach(async (to, from, next) => {
  const redirectUrl = process.env.VUE_APP_REDIRECT_URL;
  const authStore = useAuthStore();
  const isLoggedIn = authStore.loggedIn;

  // Handle Login Logic (redirect from SSO)
  if (to.query.token) {
    const result = await authStore.login(to.query.token);
    if (result.success) {
      // Remove only the SSO 'token' param from the URL, preserve others (e.g. tkn)
      // eslint-disable-next-line
      const { token: _removed, ...remainingQuery } = to.query;
      const { mainAccess, procurementAccess } = getUserDashboardAccess(authStore.currentUser);

      if (mainAccess) {
        return next({ path: "/", query: remainingQuery, replace: true });
      } else if (procurementAccess) {
        return next({ path: "/procurement", query: remainingQuery, replace: true });
      } else {
        return next({ path: "/purchase/tracking/search", replace: true });
      }
    } else {
      // Login failed - use error message from auth store
      const errorMsg = encodeURIComponent(result.error || "Login Failed");
      window.location.href = `${redirectUrl}?code=401&msg=${errorMsg}`;
      return;
    }
  }

  // Prevent logged-in users from visiting login page (if any)
  if (to.name === "login" && isLoggedIn) {
    const { mainAccess, procurementAccess } = getUserDashboardAccess(authStore.currentUser);
    if (mainAccess) {
      return next({ name: "default", replace: true });
    } else if (procurementAccess) {
      return next({ name: "procurement", replace: true });
    } else {
      return next({ name: "tracking-search", replace: true });
    }
  }

  // Auth Guard
  if (to.meta.authRequired) {
    // Handle page refresh scenario: token exists but user not loaded yet
    if (authStore.token && !authStore.user) {
      try {
        await authStore.validate();
      } catch (e) {
        console.error("Token validation failed:", e);
      }
    }

    if (authStore.loggedIn && authStore.currentUser) {
      const { mainAccess, procurementAccess, hasPrUserRecord } = getUserDashboardAccess(authStore.currentUser);
      const isProcurementRoute = to.path.startsWith("/procurement") || to.meta.dashboard === "procurement";
      const isTrackingRoute = to.path.startsWith("/purchase/tracking");

      if (!isTrackingRoute) {
        if (isProcurementRoute && !procurementAccess) {
          let alertMsg = "Anda tidak memiliki hak akses ke Procurement Dashboard.";
          if (!hasPrUserRecord || (!mainAccess && !procurementAccess)) {
            alertMsg = "Akun Anda tidak memiliki hak akses ke dashboard aplikasi. Silakan gunakan fitur Tracking Search.";
          }

          showAccessDeniedAlert(alertMsg, () => {
            if (mainAccess) {
              router.push("/");
            } else {
              router.push("/purchase/tracking/search");
            }
          });
          return;
        } else if (!isProcurementRoute && !mainAccess) {
          let alertMsg = "Anda tidak memiliki hak akses ke Main Administrator Dashboard.";
          if (!hasPrUserRecord || (!mainAccess && !procurementAccess)) {
            alertMsg = "Akun Anda tidak memiliki hak akses ke dashboard aplikasi. Silakan gunakan fitur Tracking Search.";
          }

          showAccessDeniedAlert(alertMsg, () => {
            if (procurementAccess) {
              router.push("/procurement");
            } else {
              router.push("/purchase/tracking/search");
            }
          });
          return;
        }
      }

      // If this is a tkn-based PR link, resolve it to IDs before rendering
      if (to.query.tkn && to.name === "purchase-request-bulk-show") {
        try {
          const result = await prService.resolveToken(to.query.tkn);

          // If token resolution fails (e.g. already approved/expired) or no IDs found
          if (!result?.success || !result.ids?.length) {
            await Swal.fire({
              icon: "warning",
              title: "Access Denied",
              text:
                result?.message || "Token is invalid or has already expired.",
              confirmButtonText: "OK",
            });
            return next({
              name: "purchase-request-tracking",
              query: { tkn: to.query.tkn },
              replace: true,
            });
          }

          // Access check: the logged-in user must be the intended approver and have menu access
          const currentUsername = (
            authStore.currentUser?.username || ""
          ).toUpperCase();

          const menuAccess =
            authStore.currentUser?.menu_access?.purrequest || {};

          if (menuAccess.acc_menu == 0 || menuAccess.acc_menu == "0") {
            console.warn(
              "Redirecting " +
                currentUsername +
                " non-approver or unauthorized user to public tracking page",
            );
            return next({
              name: "purchase-request-tracking",
              query: { tkn: to.query.tkn },
              replace: true,
            });
          }

          // Redirect to nos-based URL, dropping the tkn param
          return next({
            name: "purchase-request-bulk-show",
            query: { nos: result.nos.join(",") },
            replace: true,
          });
        } catch (e) {
          console.error("Token resolution failed:", e);
          return next({ name: "purchase-request", replace: true });
        }
      }

      // Already authenticated with both token and user
      return next();
    } else if (to.query.tkn && to.name === "purchase-request-bulk-show") {
      // If user is not logged in but there's a tkn param, redirect to public tracking page
      return next({
        name: "purchase-request-tracking",
        query: { tkn: to.query.tkn },
        replace: true,
      });
    }

    // Dev bypass: auto-login with dev token instead of redirecting to landing page
    const devToken = process.env.VUE_APP_DEV_TOKEN;
    if (devToken) {
      const result = await authStore.login(devToken);
      if (result.success) {
        return next();
      }
    }

    // Redirect to login if all checks fail
    window.location.href = `${redirectUrl}?code=401&msg=UnknownError`;
    return;
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta?.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error("Redirected"));
            } else resolve();
          });
        } else resolve();
      });
    }
  } catch (e) {
    return; // silently exit if redirected
  }

  document.title = `${routeTo.meta.title} | ${appConfig.title}`;
  next();
});

export default router;
